import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaPhone, FaArrowLeft, FaUserShield, FaShieldAlt, FaRocket } from 'react-icons/fa';
import { FiMail, FiLock, FiPhone, FiArrowLeft } from 'react-icons/fi';
import Header from '../components/Header';

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_API_URL;

const LoginPage = () => {
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'otp'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    otp: '',
    rememberMe: false
  });
  const [step, setStep] = useState(1); // 1: Enter credentials, 2: Enter OTP
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [showForceLogout, setShowForceLogout] = useState(false);
  const [forceLoading, setForceLoading] = useState(false);

  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  // Countdown timer
  useEffect(() => {
    let timer;
    if (timerActive && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setTimerActive(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, timerActive]);

  const startCountdown = (seconds) => {
    setCountdown(seconds);
    setTimerActive(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Request OTP for login (OTP method)
  const requestLoginOTP = async () => {
    if (!formData.phone) {
      setError('Please enter your phone number');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Send to /api/auth/login/otp (generateLoginOTP)
      const response = await axios.post('/api/auth/login/otp', {
        phone: formData.phone
      });
      
      if (response.data.success) {
        setStep(2);
        startCountdown(600); // 10 minutes for login OTP
        setSuccess('OTP sent to your registered email and phone. Please check and enter below.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Password login
  const passwordLogin = async () => {
    if (!formData.email || !formData.password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Send to /api/auth/login (loginUser)
      const response = await axios.post('/api/auth/login', {
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe
      });
      
      if (response.data.success) {
        handleLoginSuccess(response.data);
      }
    } catch (err) {
      const data = err.response?.data;

      // Show force logout button if already logged in elsewhere
      if (data?.code === 'ALREADY_LOGGED_IN' || data?.canForceLogin) {
        setError(data.error || 'Already logged in on another device');
        setShowForceLogout(true);
      } else {
        setError(data?.error || 'Login failed');
        setShowForceLogout(false);
      }
    } finally {
      setLoading(false);
    }
  };


  // Verify OTP for login
const verifyLoginOTP = async () => {
  if (formData.otp.length !== 6) {
    setError('Please enter 6-digit OTP');
    return;
  }

  setOtpLoading(true);
  setError('');

  try {
    // Send to /api/auth/login/verify (verifyOTPAndLogin)
    const response = await axios.post('/api/auth/login/verify', {
      phone: formData.phone,
      otp: formData.otp
    });

    if (response.data.success) {
      handleLoginSuccess(response.data);
    }
  } catch (err) {
    const data = err.response?.data;

    // Show force logout button if already logged in elsewhere
    if (data?.canForceLogin || data?.code === 'ALREADY_LOGGED_IN') {
      setError(data.error || 'Already logged in on another device');
      setShowForceLogout(true);
    } else {
      setError(data?.error || 'Login failed');
      setShowForceLogout(false);
    }
  } finally {
    setOtpLoading(false);
  }
};

  // Handle successful login
  // Handle successful login
  const handleLoginSuccess = (responseData) => {
    const { tokens, user } = responseData.data;

    // Save tokens and user info
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('rememberMe', formData.rememberMe.toString());
    
    // Optional: save token expiry if provided
    if (tokens.expiresIn) localStorage.setItem('tokenExpiry', tokens.expiresIn);

    // Set axios default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;

    // Trigger Header update in all tabs
    window.dispatchEvent(new Event('storage'));

    setSuccess('Login successful! Redirecting...');
    
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 1500);
  };

  // Updated forceLogin function
  const forceLogin = async () => {
    setForceLoading(true);
    setError('');

    try {
      let response;

      if (loginMethod === 'password') {
        response = await axios.post('/api/auth/login/force', {
          email: formData.email,
          password: formData.password,
          rememberMe: formData.rememberMe,
          method: 'password'
        });
      } else {
        response = await axios.post('/api/auth/login/otp/force', {
          phone: formData.phone,
          otp: formData.otp,
          method: 'otp'
        });
      }

      if (response.data.success) {
        handleLoginSuccess(response.data); // Reuse same success handler
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Force login failed');
    } finally {
      setForceLoading(false);
      setShowForceLogout(false);
    }
  };

  // Resend OTP
  const resendOTP = async () => {
    setOtpLoading(true);
    setError('');

    try {
      // Send to /api/auth/login/otp again (generateLoginOTP)
      const response = await axios.post('/api/auth/login/otp', {
        phone: formData.phone
      });

      if (response.data.success) {
        setSuccess('New OTP sent successfully!');
        startCountdown(600);
        setFormData({ ...formData, otp: '' });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loginMethod === 'password') {
      await passwordLogin();
    } else if (loginMethod === 'otp' && step === 1) {
      await requestLoginOTP();
    } else if (loginMethod === 'otp' && step === 2) {
      await verifyLoginOTP();
      
    }
  };

  // Generate OTP inputs array
  const otpInputs = [...Array(6)].map((_, index) => ({
    id: index,
    value: formData.otp[index] || ''
  }));

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps for OTP Login */}
          {loginMethod === 'otp' && (
            <div className="mb-8">
              <div className="flex items-center justify-center space-x-4">
                <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                    1
                  </div>
                  <span className="ml-2 font-medium">Enter Phone</span>
                </div>
                <div className="w-24 h-1 bg-gray-300"></div>
                <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                    2
                  </div>
                  <span className="ml-2 font-medium">Enter OTP</span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="md:flex">
              {/* Left Side - Illustration/Info */}
              <div className="hidden md:block md:w-2/5 bg-gradient-to-br from-blue-600 to-blue-600 p-10">
                <div className="h-full flex flex-col justify-center">
                  <div className="text-white">
                    <h2 className="text-3xl font-bold mb-6">Welcome Back!</h2>
                    <div className="space-y-6">
                      <div className="flex items-start">
                        <FaUserShield className="text-white text-xl mt-1 mr-3" />
                        <div>
                          <h4 className="font-semibold">Secure Access</h4>
                          <p className="text-blue-100 text-sm">Military-grade encryption for all logins</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <FaShieldAlt className="text-white text-xl mt-1 mr-3" />
                        <div>
                          <h4 className="font-semibold">Two-Factor Authentication</h4>
                          <p className="text-blue-100 text-sm">Extra layer of security with OTP</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <FaRocket className="text-white text-xl mt-1 mr-3" />
                        <div>
                          <h4 className="font-semibold">Instant Access</h4>
                          <p className="text-blue-100 text-sm">Get back to your dashboard in seconds</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8">
                    <div className="w-64 h-64 mx-auto bg-gradient-to-br from-blue-400 to-purple-400 rounded-full opacity-20"></div>
                  </div>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="md:w-3/5 p-8 md:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900">
                    {step === 1 ? 'Sign In to Your Account' : 'Verify Your Identity'}
                  </h2>
                  <p className="text-gray-600 mt-2">
                    {loginMethod === 'password' 
                      ? 'Enter your credentials to continue'
                      : step === 1 
                        ? 'Choose your preferred login method'
                        : `Enter the 6-digit code sent to your registered email/phone`
                    }
                  </p>
                </div>

                {/* Login Method Selector Tabs */}
                <div className="mb-8">
                  <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
                    <button
                      type="button"
                      onClick={() => {
                        setLoginMethod('password');
                        setStep(1);
                        setError('');
                        setSuccess('');
                      }}
                      className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all duration-300 ${
                        loginMethod === 'password'
                          ? 'bg-gradient-to-r from-blue-500 to-blue-500 text-white shadow-lg transform -translate-y-0.5'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <FaLock className="text-sm" />
                        <span>Password Login</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLoginMethod('otp');
                        setStep(1);
                        setError('');
                        setSuccess('');
                      }}
                      className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all duration-300 ${
                        loginMethod === 'otp'
                          ? 'bg-gradient-to-r from-blue-500 to-blue-500 text-white shadow-lg transform -translate-y-0.5'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <FaPhone className="text-sm" />
                        <span>OTP Login</span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Error/Success Messages */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700 font-medium">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {showForceLogout && (
                  <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
                    <p className="text-sm text-yellow-800 mb-3">
                      Your account is logged in on another device.
                    </p>
                    <button
                      onClick={forceLogin}
                      disabled={forceLoading}
                      className="w-full py-3 px-4 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all"
                    >
                      {forceLoading ? 'Logging out other device...' : 'Logout other device & Login here'}
                    </button>
                  </div>
                )}


                {success && (
                  <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-green-700 font-medium">{success}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form className="space-y-6" onSubmit={handleSubmit}>
                  {/* Step 1: Email/Phone Input */}
                  {step === 1 && (
                    <div className="space-y-6">
                      {/* Email for Password Login */}
                      {loginMethod === 'password' && (
                        <div>
                          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                            <FaEnvelope className="mr-2 text-blue-600" />
                            Email Address *
                          </label>
                          <div className="relative">
                            <input
                              name="email"
                              type="email"
                              required
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              placeholder="you@example.com"
                              value={formData.email}
                              onChange={handleChange}
                              disabled={loading}
                            />
                            <FiMail className="absolute left-3 top-3.5 text-gray-400" />
                          </div>
                        </div>
                      )}

                      {/* Phone for OTP Login */}
                      {loginMethod === 'otp' && (
                        <div>
                          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                            <FaPhone className="mr-2 text-blue-600" />
                            Phone Number *
                          </label>
                          <div className="relative">
                            <input
                              name="phone"
                              type="tel"
                              required
                              pattern="[0-9]{10}"
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              placeholder="9319745997"
                              value={formData.phone}
                              onChange={handleChange}
                              disabled={loading}
                            />
                            <FiPhone className="absolute left-3 top-3.5 text-gray-400" />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Enter your registered 10-digit phone number
                          </p>
                        </div>
                      )}

                      {/* Password Field (only for password login) */}
                      {loginMethod === 'password' && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="flex items-center text-sm font-medium text-gray-700">
                              <FaLock className="mr-2 text-blue-600" />
                              Password *
                            </label>
                            {/* <Link to="/" className="text-xs font-medium text-blue-600 hover:text-blue-500">
                              Forgot password?
                            </Link> */}
                          </div>
                          <div className="relative">
                            <input
                              name="password"
                              type="password"
                              required
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              placeholder="••••••••"
                              value={formData.password}
                              onChange={handleChange}
                              disabled={loading}
                            />
                            <FiLock className="absolute left-3 top-3.5 text-gray-400" />
                          </div>
                        </div>
                      )}
 
                      {/* Remember Me Checkbox */}
                      <div className="flex items-center">
                        <input
                          id="rememberMe"
                          name="rememberMe"
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={formData.rememberMe}
                          onChange={handleChange}
                          disabled={loading}
                        />
                        <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
                          Remember me for 30 days
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Step 2: OTP Input for OTP Login */}
                  {step === 2 && loginMethod === 'otp' && (
                    <div className="space-y-8">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-4">
                          Enter 6-digit verification code
                        </label>
                        <div className="flex justify-center space-x-3">
                          {otpInputs.map((input, index) => (
                            <input
                              key={input.id}
                              type="text"
                              inputMode="numeric"
                              maxLength="1"
                              className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
                              value={input.value}
                              onChange={(e) => {
                                const newOtp = formData.otp.split('');
                                newOtp[index] = e.target.value.replace(/[^0-9]/g, '');
                                setFormData({ ...formData, otp: newOtp.join('') });
                                
                                // Auto-focus next input
                                if (e.target.value && index < 5) {
                                  const nextInput = document.getElementById(`otp-${index + 1}`);
                                  if (nextInput) nextInput.focus();
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Backspace' && !formData.otp[index] && index > 0) {
                                  const prevInput = document.getElementById(`otp-${index - 1}`);
                                  if (prevInput) prevInput.focus();
                                }
                              }}
                              id={`otp-${index}`}
                              disabled={otpLoading}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Timer */}
                      <div className="text-center">
                        {timerActive ? (
                          <p className="text-sm text-gray-600">
                            Code expires in{' '}
                            <span className="font-semibold text-blue-600">
                              {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
                            </span>
                          </p>
                        ) : (
                          <p className="text-sm text-red-600 font-medium">
                            OTP has expired
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-4">
                    <button
                      type="submit"
                      disabled={loading || otpLoading || (loginMethod === 'otp' && step === 2 && formData.otp.length !== 6)}
                      className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {(loading || otpLoading) ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {loginMethod === 'otp' && step === 2 ? 'Verifying...' : 'Processing...'}
                        </div>
                      ) : (
                        loginMethod === 'otp' 
                          ? (step === 1 ? 'Send OTP' : 'Verify & Login')
                          : 'Sign In'
                      )}
                    </button>

                    {/* Resend OTP Button */}
                    {loginMethod === 'otp' && step === 2 && (
                      <button
                        type="button"
                        onClick={resendOTP}
                        disabled={timerActive || otpLoading}
                        className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {timerActive ? `Resend OTP (${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')})` : 'Resend OTP'}
                      </button>
                    )}

                    {/* Back Button for OTP Login Step 2 */}
                    {loginMethod === 'otp' && step === 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          setStep(1);
                          setError('');
                          setSuccess('');
                        }}
                        className="w-full py-3 px-4 text-blue-600 hover:text-blue-700 font-medium rounded-lg hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center"
                      >
                        <FiArrowLeft className="mr-2" />
                        Back to Phone Entry
                      </button>
                    )}
                  </div>
                </form>

                {/* Bottom Links */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                    <p className="text-sm text-gray-600">
                      Don't have an account?{' '}
                      <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                        Sign up here
                      </Link>
                    </p>
                    <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
                      <FiArrowLeft className="mr-2" />
                      Back to Home
                    </Link>
                  </div>
                </div>

                {/* Security Note */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700 text-center">
                    <span className="font-semibold">Secure Login:</span>{' '}
                    Your login is protected with end-to-end encryption and OTP verification.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;